import { useState, useEffect } from "react";
import { Button, TemplateEditorPanel, SMS_SOFT_LIMIT } from "@components";

export default function EditorPage({
  control,
  errors,
  watch,
  getValues,
  setValue,
  handleSubmit,
  merchant,
  isEditMode,
  loading,
  activeTab,
  setActiveTab,
  emailEnabled,
  smsEnabled,
  previewMode,
  setPreviewMode,
  isEdited,
  onToggleChannel,
  onBack,
  onResetEdits,
  onSaveAs,
  onSave,
  previewTokens,
  canEdit = true,
}) {
  const [validationError, setValidationError] = useState(null);
  const smsText = watch("smsText");
  const emailSubject = watch("emailSubject");
  const invoicesRequested = watch("invoicesRequested");
  const mandatory = watch("mandatory");

  useEffect(() => {
    if (validationError) setValidationError(null);
  }, [smsText, emailSubject]);

  const checkValidation = () => {
    if (emailEnabled) {
      const subject = getValues("emailSubject") || "";
      if (!subject.trim()) {
        setValidationError("Email subject line is required");
        return false;
      }
    }
    if (smsEnabled) {
      const smsText = getValues("smsText") || "";
      if (smsText.length > SMS_SOFT_LIMIT) {
        const over = smsText.length - SMS_SOFT_LIMIT;
        setValidationError(
          `SMS exceeds ${SMS_SOFT_LIMIT} character limit by ${over} ${over === 1 ? "character" : "characters"}`,
        );
        return false;
      }
    }
    setValidationError(null);
    return true;
  };

  const guardSave = (action) => () => {
    if (!checkValidation()) return;
    action();
  };

  return (
    <TemplateEditorPanel
      control={control}
      errors={errors}
      watch={watch}
      getValues={getValues}
      setValue={setValue}
      merchant={merchant}
      activeTab={activeTab}
      setActiveTab={setActiveTab}
      emailEnabled={emailEnabled}
      smsEnabled={smsEnabled}
      previewMode={canEdit ? previewMode : true}
      previewTokens={previewTokens}
      invoicesRequested={invoicesRequested}
      mandatory={mandatory}
      onToggleChannel={canEdit ? onToggleChannel : undefined}
      onSetPreviewMode={canEdit ? setPreviewMode : undefined}
      formProps={{ onSubmit: handleSubmit(onSave) }}
      renderFooter={() => (
        <div className="shrink-0 border-t border-zinc-200 bg-white">
          {validationError && canEdit && (
            <div className="px-4 pt-2 text-xs text-red-500 text-right">
              {validationError}
            </div>
          )}
          <div className="p-4 flex items-center gap-2">
            <Button onClick={onBack} disabled={loading} color="secondary">
              Back
            </Button>
            {canEdit && (
              <div className="ml-auto flex items-center gap-2">
                {isEdited && (
                  <Button
                    onClick={() => {
                      setValidationError(null);
                      onResetEdits();
                    }}
                    disabled={loading}
                    color="secondaryLink"
                  >
                    Reset
                  </Button>
                )}
                {isEdited && (
                  <Button
                    onClick={guardSave(onSaveAs)}
                    disabled={loading}
                    color="secondary"
                  >
                    Save as...
                  </Button>
                )}
                <Button
                  onClick={guardSave(() => handleSubmit(onSave)())}
                  color="primary"
                  submitting={loading}
                >
                  {isEditMode ? "Save changes" : "Create template"}
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    />
  );
}
